import { DebrisBadge } from '@features/ui-atoms/components/DebrisBadge';
import { PrivateSstBadge } from '@features/ui-atoms/components/LockFeedbackBadge';
import { ChevronRight } from '@features/ui-atoms/components/Navigation';
import PublicationInfo from '@features/ui-atoms/components/PublicationInfo';
import Tooltip from '@features/ui-atoms/components/Tooltip';
import { ChatOutlined, LiveHelpOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { styled } from '@mui/joy';
import Typography from '@mui/joy/Typography';
import { spaceObjectQueries } from '@root/src/entities/SpaceObject/queries';
import { SST_TYPES, SstType } from '@root/src/entities/Sst/sstType/enum';
import { GroupedEvents, SstFront } from '@root/src/entities/Sst/type';
import { formatUnknownId } from '@root/src/utils/formatters/strings';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import SstEventBadge from './SstEventBadge';

const SstBlock = ({
  spaceObjectId,
  spaceObject,
  critical,
  createdAt,
  feedbackRequested,
  updatedAt,
  createdBy: { memberId },
  comments,
  sstEvents,
  unknownSpaceObjectId,
  feedbackRequestedFrom,
  isDebris,
}: SstFront) => {
  const { t } = useTranslation('common');

  // Fallback only: lists now inline the space object (expand=spaceObject),
  // so this per-card request fires just for SSTs whose object came as a bare
  // id (older cache entries, undiscoverable objects).
  const { data: fetchedSpaceObject } = useQuery({
    ...spaceObjectQueries.detail(spaceObjectId as string),
    staleTime: 30000,
    enabled: !!spaceObjectId && !unknownSpaceObjectId && !spaceObject,
    // Undiscoverable objects 403/404 here and stay cached as errored queries;
    // a window-focus event would refire all of them at once (same storm class
    // as the member-byline 404s that OOM-killed UAT). Don't refire stable
    // failures automatically.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const getSpaceObjectName = () => {
    if (unknownSpaceObjectId) {
      return `${t('sst.isUnknownSpaceObject.label')} #${formatUnknownId(unknownSpaceObjectId)}`;
    }

    return spaceObject?.name || fetchedSpaceObject?.name || '';
  };

  const groupedEvents: GroupedEvents | undefined = sstEvents?.reduce((acc, event) => {
    const { sstType } = event;
    if (!acc[sstType]) {
      acc[sstType] = [];
    }
    if (sstType === SST_TYPES.OPERATION) {
      acc[sstType]?.push(event.operationType);
    }
    return acc;
  }, {} as GroupedEvents);

  return (
    <Card>
      <div style={{ flex: 1, display: 'flex', gap: '6px', flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Typography level="small-medium">{getSpaceObjectName()}</Typography>
          {/* The state this block published, not the object's current one: an
              older block stays "no debris" after a later one flags the object. */}
          {isDebris && <DebrisBadge />}
          <PrivateSstBadge feedbackRequestedFrom={feedbackRequestedFrom} />
          <PublicationInfo updatedAt={updatedAt} createdAt={createdAt} memberId={memberId} />
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {groupedEvents &&
            Object.keys(SST_TYPES).map(
              sstType =>
                groupedEvents[sstType as SstType] && (
                  <SstEventBadge
                    key={sstType}
                    sstType={sstType as SstType}
                    operationTypes={groupedEvents[sstType as SstType] as string[]}
                  />
                ),
            )}
        </div>
      </div>
      <TagsContainer>
        {critical && <WarningTag />}
        {feedbackRequested && <QuestionTag />}
        {comments.length > 0 && <ChatTag />}
      </TagsContainer>
      <div>
        <ChevronRight />
      </div>
    </Card>
  );
};

export default SstBlock;

const Card = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'white',
  padding: '8px 16px 8px 16px',
  gap: '12px',
  cursor: 'pointer',
  borderRadius: '3px',
  height: '62px',
  '&:hover': {
    boxShadow: '4px 4px 4px rgba(19, 19, 103, 0.08)',
  },
}));

const TagsContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

const baseTagStyle = {
  width: '28px',
  height: '24px',
  borderRadius: '8px',
  padding: '4px 6px 4px 6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const OrangeTag = styled('div')(({ theme: { palette } }) => ({
  ...baseTagStyle,
  backgroundColor: palette.orange3,
  '& > svg': {
    height: '16px',
    width: '16px',
    color: palette.orange1,
  },
}));

const GreyTag = styled('div')(({ theme: { palette } }) => ({
  ...baseTagStyle,
  backgroundColor: palette.grey1,
  '& > svg': {
    height: '16px',
    width: '16px',
    color: palette.grey4,
  },
}));

const WarningTag = () => {
  const { t } = useTranslation('common');
  return (
    <Tooltip title={t('sst.critical.tooltip')}>
      <OrangeTag>
        <WarningAmberOutlined />
      </OrangeTag>
    </Tooltip>
  );
};

const QuestionTag = () => {
  const { t } = useTranslation('common');
  return (
    <Tooltip title={t('sst.feedbackRequested.tooltip')}>
      <OrangeTag>
        <LiveHelpOutlined />
      </OrangeTag>
    </Tooltip>
  );
};

const ChatTag = () => {
  const { t } = useTranslation('common');
  return (
    <Tooltip title={t('sst.comments.icon.tooltip')}>
      <GreyTag>
        <ChatOutlined />
      </GreyTag>
    </Tooltip>
  );
};
